import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';

interface CustomAnalysisDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToggle?: boolean;
  showHeader?: boolean;
}

const CustomAnalysisDisplay: React.FC<CustomAnalysisDisplayProps> = ({
  result,
  theme = 'light',
  showToggle = false,
  showHeader = true
}) => {
  const [showRaw, setShowRaw] = useState(false);

  // Check if the result looks like a custom analysis (plain text string, not a structured object)
  const isCustomAnalysisResult = (data: any) => {
    if (!data) return false;

    // If it's a simple string, it's likely custom analysis
    if (typeof data === 'string') {
      return true;
    }

    // If it's an object with very specific structure from other tasks, reject it
    if (typeof data === 'object') {
      // Check for other task-specific structures
      const keys = Object.keys(data);

      // Parts list structure
      if (keys.some(k => /^\d+$/.test(k)) && data[keys[0]]?.description) {
        return false;
      }

      // Claims support structure
      if (keys.some(k => /^claim_\d+$/.test(k))) {
        return false;
      }

      // Spec map structure
      if (data.elements || data.title || data.features) {
        return false;
      }

      // Translation structure
      if (data.translated_sections || (data.source_language && data.target_language)) {
        return false;
      }
    }

    return false;
  };

  if (!isCustomAnalysisResult(result)) {
    return null; // Not a custom analysis result
  }

  const analysisText = typeof result === 'string' ? result : JSON.stringify(result, null, 2);

  const exportToDocx = async () => {
    try {
      const children: Paragraph[] = [];

      // Title
      children.push(
        new Paragraph({
          text: "Custom Analysis",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Process the analysis text - split by lines and handle markdown-style formatting
      const lines = analysisText.split('\n');

      lines.forEach((line: string) => {
        const trimmedLine = line.trim();

        if (!trimmedLine) {
          // Empty line - add spacing
          children.push(new Paragraph({ text: '', spacing: { after: 100 } }));
          return;
        }

        // Check for markdown headers
        if (trimmedLine.startsWith('### ')) {
          children.push(
            new Paragraph({
              text: trimmedLine.replace('### ', ''),
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 200, after: 100 },
            })
          );
        } else if (trimmedLine.startsWith('## ')) {
          children.push(
            new Paragraph({
              text: trimmedLine.replace('## ', ''),
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 300, after: 150 },
            })
          );
        } else if (trimmedLine.startsWith('# ')) {
          children.push(
            new Paragraph({
              text: trimmedLine.replace('# ', ''),
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 },
            })
          );
        } else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
          // Bullet points
          children.push(
            new Paragraph({
              text: `• ${trimmedLine.substring(2)}`,
              spacing: { after: 50 },
              indent: { left: 360 },
            })
          );
        } else if (/^\d+\.\s/.test(trimmedLine)) {
          // Numbered list
          children.push(
            new Paragraph({
              text: trimmedLine,
              spacing: { after: 50 },
              indent: { left: 360 },
            })
          );
        } else {
          // Regular paragraph - handle inline bold (**text**)
          const parts = trimmedLine.split(/(\*\*[^*]+\*\*)/);
          const runs: TextRun[] = [];

          parts.forEach((part: string) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              runs.push(new TextRun({ text: part.slice(2, -2), bold: true }));
            } else if (part) {
              runs.push(new TextRun({ text: part }));
            }
          });

          if (runs.length > 0) {
            children.push(
              new Paragraph({
                children: runs,
                spacing: { after: 100 },
              })
            );
          }
        }
      });

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'custom-analysis.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting custom analysis to DOCX:', error);
    }
  };

  const renderFormattedAnalysis = () => {
    // Split by markdown headers and paragraphs
    const lines = analysisText.split('\n');

    const renderInlineBold = (text: string) => {
      // Split by bold markers but keep them for proper rendering
      const parts = text.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={i} className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>
              {part.slice(2, -2)}
            </strong>
          );
        }
        return <span key={i}>{part}</span>;
      });
    };

    const formattedLines = lines.map((line, index) => {
      const trimmedLine = line.trim();

      // Handle markdown-style headers
      if (trimmedLine.startsWith('###')) {
        return (
          <h4
            key={index}
            className={`text-base font-semibold mt-4 mb-2 ${
              theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
            }`}
          >
            {trimmedLine.replace(/^###\s*/, '')}
          </h4>
        );
      } else if (trimmedLine.startsWith('##')) {
        return (
          <h3
            key={index}
            className={`text-lg font-semibold mt-5 mb-3 ${
              theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
            }`}
          >
            {trimmedLine.replace(/^##\s*/, '')}
          </h3>
        );
      } else if (trimmedLine.startsWith('#')) {
        return (
          <h2
            key={index}
            className={`text-xl font-semibold mt-6 mb-3 ${
              theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
            }`}
          >
            {trimmedLine.replace(/^#\s*/, '')}
          </h2>
        );
      }
      // Handle numbered list items (e.g., "1. Patent Application...")
      else if (/^\d+\.\s/.test(trimmedLine)) {
        const content = trimmedLine.replace(/^\d+\.\s*/, '');
        return (
          <div
            key={index}
            className={`font-semibold mt-4 mb-2 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}
          >
            {renderInlineBold(content)}
          </div>
        );
      }
      // Handle bullet points starting with "-" or "*"
      else if (trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
        // Remove the leading dash/asterisk
        const content = trimmedLine.replace(/^[-*]\s*/, '');
        return (
          <li
            key={index}
            className={`ml-6 my-1 leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
          >
            {renderInlineBold(content)}
          </li>
        );
      }
      // Empty lines
      else if (trimmedLine === '') {
        return <div key={index} className="h-2" />;
      }
      // Regular paragraphs
      else {
        return (
          <p
            key={index}
            className={`my-2 leading-relaxed ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            {renderInlineBold(line)}
          </p>
        );
      }
    });

    return <div className="space-y-1">{formattedLines}</div>;
  };

  const renderRawData = () => {
    return (
      <pre
        className={`text-sm whitespace-pre-wrap font-mono p-4 rounded-md overflow-y-auto ${
          theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
        }`}
      >
        {analysisText}
      </pre>
    );
  };

  return (
    <div className={showHeader ? 'space-y-3' : ''}>
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            📄 Analysis Output
          </div>
          {showToggle && (
            <div className="flex items-center gap-2">
              <button
                onClick={exportToDocx}
                className={`text-xs py-1 px-3 rounded-md transition-colors flex items-center gap-1 ${
                  theme === 'dark' ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-green-500 text-white hover:bg-green-600'
                }`}
                title="Export analysis as Word document"
              >
                <Download className="w-3 h-3" />
                Export DOCX
              </button>
              <button
                onClick={() => setShowRaw(!showRaw)}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  showRaw
                    ? theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    : theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {showRaw ? 'Show Formatted' : 'Show Raw'}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
        <div
          className={`p-4 rounded-lg border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          {showRaw ? renderRawData() : renderFormattedAnalysis()}
        </div>
      </div>
    </div>
  );
};

export default CustomAnalysisDisplay;
